/*eslint no-unused-vars: ["error", { "caughtErrors": "none", "argsIgnorePattern": "^_" }]*/
/**
 * Vue plugin to manage authentication with Doggo and Cognito.
 * @module Doggo
 */

import { Amplify, Auth, Hub } from "aws-amplify"
// import { signIn, signOut, fetchAuthSession, getCurrentUser, signInWithRedirect, } from 'aws-amplify/auth';

import * as Vue from "vue"
// import { ref } from "vue"

/**
 * Whether the current URL looks like a Cognito Hosted UI OAuth redirect
 * (ie. a `code`/`state` pair Amplify still needs to exchange for tokens).
 * @returns {boolean}
 */
function isOAuthRedirectInProgress() {
  const params = new URLSearchParams(window.location.search)
  return params.has("code") && params.has("state")
}

/**
 * Waits for Amplify to finish processing a pending Hosted UI OAuth
 * response (success or failure) via the Hub 'auth' channel, so callers
 * don't race `Auth.currentAuthenticatedUser()` against the in-flight
 * token exchange. Resolves on its own after `timeoutMs` as a safety net
 * in case the expected event never fires.
 * @param {number} [timeoutMs=8000]
 * @returns {Promise<void>}
 */
function waitForHostedUIResult(timeoutMs = 8000) {
  return new Promise((resolve) => {
    let settled = false
    const finish = () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      unsubscribe()
      resolve()
    }
    const unsubscribe = Hub.listen("auth", ({ payload: { event } }) => {
      if (
        [
          "cognitoHostedUI",
          "cognitoHostedUI_failure",
          "signIn",
          "signIn_failure",
        ].includes(event)
      ) {
        finish()
      }
    })
    const timer = setTimeout(finish, timeoutMs)
  })
}

/**
 * This namespace is accessible on the Vue prototype after installation.
 * @namespace $auth
 * @example
 * // Accessible in a component
 * async beforeMount() {
 *     await this.$auth.login()
 * },
 */
const $auth = {
  /**
   * AWS Amplify API from {@link https://aws-amplify.github.io/amplify-js/api/classes/authclass.html|@aws-amplify/auth}
   */
  amplifyAuth: Auth,

  /**
   * Whether a user is logged in.
   * @type {boolean}
   */
  authenticated: false,

  /**
   * Whether a user is authorized to use the app, ie has the `user` role.
   * @type {boolean}
   */
  authorized: false,

  /**
   * The current logged in user.
   * @type {object}
   */
  user: null,

  /**
   * The ID for the session refresh setInterval.
   * @type {number}
   */
  refreshInterval: null,

  /**
   * Returns the current Cognito session and refreshes authenticated status.
   * @returns {Promise<object|null>} session
   * @private
   */
  getSession() {
    return Amplify.Auth.currentSession()
      .then((session) => {
        this.authenticated = true
        return session
      })
      .catch(() => {
        this.authenticated = false
        return null
      })
  },

  /**
   * Returns the Cognito Identity JWT, used for authorization.
   * @returns {Promise<string>} JWT - base64-encoded JSON Web Token
   * @private
   */
  async jwt() {
    const session = await this.getSession()
    if (session) {
      return session.getIdToken().getJwtToken()
    } else {
      return ""
    }
  },

  /**
   * Get the claims of the logged in user.
   * @returns {Promise<object|null>} user
   * @private
   */
  async getUser() {
    const session = await this.getSession()
    if (session) {
      const user = session.getIdToken().payload
      user.roles = JSON.parse(user.roles)
      return user
    } else {
      return null
    }
  },

  /**
   * Login user via Shibboleth.
   * @returns {Promise<object>} user
   */
  async login() {
    try {
      if (isOAuthRedirectInProgress()) {
        // Amplify is still exchanging the code for tokens in the
        // background; wait for that to settle instead of racing it.
        await waitForHostedUIResult()
      }
      await Amplify.Auth.currentAuthenticatedUser()
    } catch (error) {
      // No user? Login via Shibboleth.
      console.log("Redirecting to WebAuth...")
      Amplify.Auth.federatedSignIn({ provider: "ua-shibboleth" })
      return new Promise((r) => setTimeout(r, 5000)) // Block execution until redirect
    } finally {
      this.user = await this.getUser()
      this.authenticated = true
      this.authorized = this.hasRole("user")

      // Refresh tokens every 30min
      this.refreshInterval = setInterval(
        () => Auth.currentSession,
        30 * 60 * 1000,
      )
    }
  },

  /**
   * Destroy Cognito tokens and logout from Shibboleth.
   * Redirects to WebAuth log out page.
   */
  async logout(options = { global: false }) {
    await Amplify.Auth.signOut(options)
  },

  /**
   * Checks whether the user has a certain role.
   * @param {string} role
   * @returns {boolean} boolean
   */
  hasRole(role) {
    const clientId = Auth.userPool.clientId
    const user = this.user
    const appRoles = user && user.roles && user.roles[clientId]
    return Boolean(appRoles && appRoles.includes(role))
  },
}

/**
 * Axios interceptor which adds identity JWT `Authorization` header to requests.
 * This function checks if a JWT is available, and if so, it adds it to the `Authorization`
 * header of the outgoing request. If the common headers are not defined, it initializes
 * and sets the `Authorization` header directly under `headers`. If they are defined,
 * it sets the `Authorization` header under `headers.common`.
 *
 * @returns {object} config - axios request config
 * @example
 * const api = axios.create()
 * api.interceptors.request.use(authHeaderInterceptor)
 */
export async function authHeaderInterceptor(config) {
  const jwt = await $auth.jwt();
  // If a JWT is obtained, add it to the Authorization header
  if (jwt) {
    // Check if the common headers object exists
    if (config.headers.common === undefined) {
      // Directly set the Authorization header if common headers are undefined
      config.headers.Authorization = `Bearer ${jwt}`;
    } else {
      // Set the Authorization header under common headers
      config.headers.common["Authorization"] = `Bearer ${jwt}`;
    }
  }
  return config;
  }

/** Returns a rudimentary testing mock of the auth object. */
function getStub() {
  const stub = {
    authenticated: true,
    authorized: true,
    user: {},
    login: async () => {},
    logout: async () => {},
    hasRole: () => true,
    jwt: async () => "jwt",
  }
  const handler = {
    get: function (target, prop, _receiver) {
      if (Reflect.has(target, prop)) return Reflect.get(...arguments)
      return async () => null
    },
  }
  return new Proxy(stub, handler)
}

/**
 * @param {Vue} app - Vue instance
 * @param {boolean} [disable=false] - Mocks the auth object for testing.
 * @param {Object} amplify - Options to configure the underlying Amplify Auth instance.
 * @param {string} amplify.region - AWS Region of User Pool
 * @param {string} amplify.userPoolWebClientId - Cognito app client ID
 * @param {string} amplify.userPoolId - Cognito User Pool ID
 * @param {string} amplify.domain - Cognito Domain
 * @param {string} amplify.redirectSignIn - URL to redirect to after sign in. Must match client config exactly!
 */
async function install(app, options) {
  const amplifyConfig = {
    Auth: {
      region: options.amplify.region,
      userPoolId: options.amplify.userPoolId,
      userPoolWebClientId: options.amplify.userPoolWebClientId,
      mandatorySignIn: true,
      oauth: {
        domain: options.amplify.domain,
        scopes: ["openid", "profile", "email", "phone"],
        redirectSignIn: options.amplify.redirectSignIn,
        redirectSignOut: "https://shibboleth.arizona.edu/webauth/logout",
        responseType: "code",
      },
    },
  }

  Amplify.configure(amplifyConfig)

  let auth = options.disable ? getStub() : $auth
  const version = Number(app.version.split(".")[0])
  if (version === 3) {
    app.config.globalProperties.$auth = Vue.reactive(auth)
  } else if (version === 2) {
    app.prototype.$auth = app.observable(auth)
  }
}

export default { install }